import { Logo } from "@/components/layout/logo";
import { ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useInputFocus } from "@/hooks";
import { useIsProcessing } from "@/lib/hooks/useIsProcessing";
import { useSidebarStore } from "@/state/sidebarStore";
import { useMessageStore } from "@/state/stores";
import { type PropsWithChildren, useEffect, useRef } from "react";
import { ChatContainer } from "./ChatContainer";
import { AttachmentsSection } from "./components";

export function Chat({ children }: PropsWithChildren) {
  const isProcessing = useIsProcessing();
  const isLoading = useMessageStore((state) => state.isLoading);
  const messagesLength = useMessageStore((state) => state.messages.length);

  const hasVersions = useMessageStore(
    ({ versionInformation: versions }) => Object.keys(versions).length > 0,
  );

  const focusInput = useInputFocus();
  const initialMessageCountRef = useRef(messagesLength);
  const detachAndCollapse = useSidebarStore((state) => state.detachAndCollapse);

  // Focus the textarea when loading and thinking is complete
  useEffect(() => {
    if (!isLoading && !isProcessing) {
      focusInput();
    }
  }, [isLoading, isProcessing, focusInput]);

  // Collapse sidebar when message count increases
  useEffect(() => {
    if (messagesLength > initialMessageCountRef.current) {
      detachAndCollapse();
      initialMessageCountRef.current = messagesLength;
    }
  }, [messagesLength, detachAndCollapse]);

  return (
    <ResizablePanelGroup direction="horizontal" className="h-full">
      <ResizablePanel defaultSize={50}>
        <div className="h-full flex flex-col bg-background relative">
          <header className="border-b shrink-0">
            <div className="flex items-center justify-between h-12 px-4">
              <div className="flex items-center gap-2">
                <Logo />
              </div>
            </div>
          </header>

          <ChatContainer>{children}</ChatContainer>
        </div>
      </ResizablePanel>
      {hasVersions && <AttachmentsSection />}
    </ResizablePanelGroup>
  );
}
